import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LockOpen, Lock, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const mxn = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
const tiempo = (h) => (h < 1 ? `${Math.round(h * 60)} min` : h < 48 ? `${h.toFixed(1)} h` : `${Math.floor(h / 24)} d`);

export default function CajasAbiertas() {
  const { user } = useAuth();
  const [cajas, setCajas] = useState(null);
  const [cerrando, setCerrando] = useState("");
  const esAdmin = user?.rol === "gran_admin";

  const cargar = useCallback(() => { api.get("/cajas/abiertas").then((r) => setCajas(r.data)).catch(() => setCajas([])); }, []);
  useEffect(() => { cargar(); const t = setInterval(cargar, 60000); return () => clearInterval(t); }, [cargar]);

  async function cerrar(c) {
    const motivo = window.prompt(`Cerrar ${c.caja} de ${c.sucursal_nombre} de forma remota.\nSe generará el corte automático con el efectivo esperado (${mxn(c.efectivo_esperado)}).\nMotivo (opcional):`);
    if (motivo === null) return;
    setCerrando(c.id);
    try {
      const { data } = await api.post(`/cajas/${c.id}/cerrar-forzado`, { motivo });
      toast.success(`${c.caja} · ${c.sucursal_nombre} cerrada · corte ${data.folio}`);
      cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setCerrando(""); }
  }

  if (!cajas) return null;
  const olvidadas = cajas.filter((c) => c.olvidada).length;
  return (
    <div className="surface-card p-5" data-testid="cajas-abiertas-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="eyebrow flex items-center gap-2"><LockOpen className="w-3.5 h-3.5 text-emerald-600" /> Cajas abiertas ahora <span className="chip h-5 px-1.5 text-[10px]" data-testid="cajas-abiertas-count">{cajas.length}</span></div>
        {olvidadas > 0 && <span className="text-[11px] font-bold text-red-600 flex items-center gap-1" data-testid="cajas-olvidadas"><AlertTriangle className="w-3.5 h-3.5" /> {olvidadas} con más de 14 h</span>}
      </div>
      {cajas.length === 0 ? <p className="text-sm text-brand-muted">No hay cajas abiertas en la red.</p> : (
        <div className="space-y-2">
          {cajas.map((c) => (
            <div key={c.id} data-testid={`caja-abierta-${c.id}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${c.olvidada ? "bg-red-50" : "bg-brand-canvas"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 text-sm"><span className="font-semibold text-brand-ink truncate">{c.sucursal_nombre}</span><span className="chip h-5 px-1.5 text-[10px]">{c.caja}</span></div>
                <div className="text-[11px] text-brand-muted truncate">Abrió {c.abierto_por_nombre} · hace <strong className={c.olvidada ? "text-red-600" : ""}>{tiempo(c.horas_abierta)}</strong> · {c.num_ventas} ventas · {mxn(c.total_ventas)} · efectivo esperado {mxn(c.efectivo_esperado)}</div>
              </div>
              {esAdmin && <button data-testid={`cerrar-forzado-${c.id}`} disabled={!!cerrando} onClick={() => cerrar(c)} className="btn-soft h-8 px-2.5 text-xs shrink-0" title="Cerrar remotamente y generar corte">{cerrando === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Cerrar</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
